import React from 'react';
import { Button } from 'antd';
import { BarcodeOutlined, BranchesOutlined, CopyOutlined, GlobalOutlined, TagOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ObjectCardHero } from '../../../components/layout/ObjectCard';
import type { ProductCategory } from '../productCategoriesApi';
import type { ProductDataGap } from '../productLogistics';
import { formatCategoryPath } from '../category/categoryHierarchy';

interface ProductCardHeaderProps {
  name?: string | null;
  englishName?: string | null;
  isActive: boolean;
  isNew: boolean;
  gaps: ProductDataGap[];
  category?: ProductCategory | null;
  ean?: string | null;
  cnCode?: string | null;
  /** Rows of the „Warianty” section: the base product plus its variants. */
  versionCount: number;
  onBack: () => void;
  /** Opens a new card prefilled from this product; absent for a new product or a read-only viewer. */
  onDuplicate?: () => void;
}

/**
 * Product hero (components/layout/ObjectCard): monogram, name as typed in
 * „Dane produktu”, status and data-gap pills, English name line and a facts
 * strip (category, EAN, CN code, variants).
 */
const ProductCardHeader: React.FC<ProductCardHeaderProps> = ({
  name, englishName, isActive, isNew, gaps, category, ean, cnCode, versionCount, onBack, onDuplicate,
}) => {
  const { t } = useTranslation(['products']);
  const title = String(name || '').trim() || (isNew ? t('products:card.newProduct') : '—');
  return (
    <ObjectCardHero
      testId="product-card-header"
      kind={t('products:card.kind')}
      name={title}
      backLabel={t('products:card.back')}
      onBack={onBack}
      badges={(
        <>
          {!isNew && (
            <span className={`obj-pill ${isActive ? 'obj-pill--success' : ''}`} data-testid="product-status-pill">
              {isActive ? t('products:card.active') : t('products:card.inactive')}
            </span>
          )}
          {gaps.length === 0
            ? <span className="obj-pill obj-pill--primary">{t('products:card.complete')}</span>
            : gaps.map((gap) => (
              <span key={gap} className="obj-pill obj-pill--warning">
                {t('products:card.missingItem', { item: t(`products:card.gaps.${gap}`) })}
              </span>
            ))}
        </>
      )}
      meta={[englishName && <><GlobalOutlined aria-hidden="true" />{englishName}</>]}
      actions={onDuplicate && (
        <Button icon={<CopyOutlined />} onClick={onDuplicate}>
          {t('products:card.duplicate')}
        </Button>
      )}
      facts={[
        {
          key: 'category',
          label: t('products:card.categoryLabel'),
          icon: <TagOutlined />,
          value: category ? formatCategoryPath(category.name, category.parentName) : '—',
        },
        { key: 'ean', label: t('products:card.ean'), icon: <BarcodeOutlined />, value: ean || '—' },
        { key: 'cn', label: t('products:card.cnCode'), icon: <GlobalOutlined />, value: cnCode || '—' },
        { key: 'versions', label: t('products:card.versionsCount'), icon: <BranchesOutlined />, value: isNew ? '—' : versionCount },
      ]}
    />
  );
};

export default ProductCardHeader;
